/**
 * How long the table has been standing still, and what to say about it.
 *
 * socket.io declares a dead socket only after `pingInterval` + `pingTimeout`,
 * and even at the shortened settings this server now uses that is on the order
 * of twenty seconds. Until then the seat is simply highlighted and nothing
 * moves: the other three players are looking at a table that has frozen, with
 * no way to tell a person thinking from a phone in a tunnel.
 *
 * So this is deliberately *decoupled from the disconnect declaration* — it
 * knows nothing about sockets, only that the match sequence has not advanced.
 * That makes it right about both cases at once, which is why the wording never
 * claims to know which one it is: "waiting…" is true of someone deciding and
 * of someone whose phone died, and the pause overlay says which soon enough.
 *
 * A guess dressed up as information would be worse than the freeze. Saying
 * "still thinking" about a player who has actually dropped is the kind of
 * small confident lie that makes people distrust the rest of the screen.
 */
import { useEffect, useRef, useState } from 'react'

/** Long enough that a normal decision never trips it — a bid takes a few
 *  seconds, a card less — and far short of the disconnect declaration. */
export const WAITING_MS = 8_000
/** Past this, something is probably wrong rather than slow. */
export const UNRESPONSIVE_MS = 22_000

/**
 * Milliseconds since the match last changed.
 *
 * Keyed on the snapshot sequence rather than on whose turn it is: `seq`
 * advances on every state change, so it resets on anything happening at all,
 * and it does not need a special case for the seat that wins a trick and then
 * leads the next one without anyone else acting in between.
 */
export function useStillMs(seq: number | null, tick = 1_000): number {
  const since = useRef(Date.now())
  const [, force] = useState(0)

  useEffect(() => {
    since.current = Date.now()
    force((n) => n + 1)
  }, [seq])

  useEffect(() => {
    const timer = window.setInterval(() => force((n) => n + 1), tick)
    return () => window.clearInterval(timer)
  }, [tick])

  return seq === null ? 0 : Date.now() - since.current
}

/** The line under a seat that is holding everyone up, or null while the wait
 *  is still an ordinary one. */
export function waitingWord(stillMs: number): string | null {
  if (stillMs >= UNRESPONSIVE_MS) return 'not responding…'
  if (stillMs >= WAITING_MS) return 'waiting…'
  return null
}
